import { Box, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { ConsumedPreset, MembershipItem } from '@spike/membership-model';
import { CounterField } from 'components/UI/V3/CounterField/CounterField';
import { Switch } from 'components/UI/V3/Forms/Switch/Switch';
import { FunctionComponent, useState } from 'react';
import Title from './Title';

export interface ConsumedPresetSectionProps {
    membershipItems: MembershipItem[];
    consumedPreset?: ConsumedPreset;
    onChange: (preset: ConsumedPreset | undefined) => void;
    className?: string;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            borderTop: '1px solid #D4D4D4',
            marginTop: 30,
            paddingTop: 24
        },
        toggleRow: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
        },
        toggleLabel: {
            fontSize: 14,
            color: '#7A7A7A',
            lineHeight: 1.4,
            marginTop: 4
        },
        itemList: {
            marginTop: 20,
            border: '1px solid #D4D4D4',
            borderRadius: 8,
            overflow: 'hidden'
        },
        itemRow: {
            'display': 'flex',
            'alignItems': 'center',
            'justifyContent': 'space-between',
            'padding': '16px',
            'borderBottom': '1px solid #D4D4D4',
            '&:last-child': {
                borderBottom: 'none'
            }
        },
        itemName: {
            fontSize: 15,
            fontWeight: 500,
            lineHeight: 1
        },
        itemSubtext: {
            fontSize: 13,
            color: '#7A7A7A',
            lineHeight: 1,
            marginTop: 4
        },
        itemControls: {
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexShrink: 0
        },
        remaining: {
            fontSize: 13,
            color: '#7A7A7A',
            lineHeight: 1,
            minWidth: 46,
            textAlign: 'right'
        },
        remainingZero: {
            color: '#BCB8AE'
        }
    })
);

export const ConsumedPresetSection: FunctionComponent<ConsumedPresetSectionProps> = props => {
    const classes = useStyles(props);

    const [enabled, setEnabled] = useState<boolean>(() => {
        return !!props.consumedPreset && Object.keys(props.consumedPreset).length > 0;
    });

    const [localValues, setLocalValues] = useState<Record<string, number>>(() => {
        return props.membershipItems.reduce((acc, item) => {
            const key = String(item.id);
            acc[key] = props.consumedPreset?.[key] ?? 0;
            return acc;
        }, {} as Record<string, number>);
    });

    const buildPreset = (values: Record<string, number>): ConsumedPreset => {
        return props.membershipItems.reduce((acc, item) => {
            const key = String(item.id);
            acc[key] = values[key] ?? 0;
            return acc;
        }, {} as ConsumedPreset);
    };

    const handleToggle = (checked: boolean) => {
        setEnabled(checked);
        props.onChange(checked ? buildPreset(localValues) : undefined);
    };

    const handleCounterChange = (itemId: string, value: number) => {
        const next = { ...localValues, [itemId]: value };
        setLocalValues(next);
        props.onChange(buildPreset(next));
    };

    return (
        <Box className={`${classes.container} ${props.className ?? ''}`}>
            <Box className={classes.toggleRow}>
                <Box>
                    <Title>Prior Usage</Title>
                    <Typography className={classes.toggleLabel}>Client migrating from another platform?</Typography>
                </Box>
                <Switch
                    checked={enabled}
                    onChange={handleToggle}
                />
            </Box>

            {enabled && props.membershipItems.length > 0 && (
                <Box className={classes.itemList}>
                    {props.membershipItems.map(item => {
                        const key = String(item.id);
                        const consumed = localValues[key] ?? 0;
                        const remaining = item.quantity - consumed;

                        return (
                            <Box
                                key={key}
                                className={classes.itemRow}
                            >
                                <Box>
                                    <Typography className={classes.itemName}>{item.itemName}</Typography>
                                    <Typography className={classes.itemSubtext}>Included: {item.quantity}</Typography>
                                </Box>
                                <Box className={classes.itemControls}>
                                    <CounterField
                                        value={consumed}
                                        minLimit={0}
                                        maxLimit={item.quantity}
                                        hasBorder={true}
                                        onChange={value => handleCounterChange(key, value)}
                                    />
                                    <Typography
                                        className={`${classes.remaining} ${
                                            remaining === 0 ? classes.remainingZero : ''
                                        }`}
                                    >
                                        {remaining} left
                                    </Typography>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
};

export default ConsumedPresetSection;
